import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import HttpClient from '../../../services/HttpClient'

const initialState = {
  adventures: [],
  adventure: null,
  joined: false,
  status: 'idle',
  error: null,
}

export const fetchAdventure = createAsyncThunk('adventures/fetchAdventure', async (slugOrOpts) => {
  const slug = typeof slugOrOpts === 'string' ? slugOrOpts : slugOrOpts?.slug
  const bustCache = typeof slugOrOpts === 'object' && !!slugOrOpts?.bustCache
  const characterId =
    typeof slugOrOpts === 'object' && slugOrOpts?.characterId != null
      ? Number(slugOrOpts.characterId)
      : null
  const params = new URLSearchParams()
  if (bustCache) params.set('_', String(Date.now()))
  if (Number.isFinite(characterId) && characterId > 0) {
    params.set('characterId', String(characterId))
  }
  const qs = params.toString() ? `?${params.toString()}` : ''
  const response = await HttpClient().get(`/api/v1/adventure/${slug}${qs}`)
  return response.data
})

export const joinAdventure = createAsyncThunk(
  'adventures/joinAdventure',
  async ({ charId, slug }, { rejectWithValue }) => {
    try {
      const response = await HttpClient().post('/api/v1/adventure/join', {
        charId,
        slug,
      })
      return { ...response.data, joined: true }
    } catch (err) {
      return rejectWithValue({
        message: err?.message || 'Error al unirse a la aventura',
        code: err?.code,
        status: err?.status,
      })
    }
  }
)

export const leaveAdventure = createAsyncThunk('adventures/leaveAdventure', async ({ charId, slug }) => {
  const response = await HttpClient().post('/api/v1/adventure/leave', {
    charId,
    slug,
  })
  return response.data
})

export const getJoined = createAsyncThunk('adventures/getJoined', async (slug) => {
  const response = await HttpClient().get('/api/v1/adventure/join/' + slug)
  return response.data
})

export const getReplies = createAsyncThunk(
  'adventures/getReplies',
  async ({ slug, limit, margin, offset = 0, viewType = 'chronological' }) => {
    const params = new URLSearchParams()
    if (limit != null) {
      params.set('limit', String(limit))
      params.set('offset', String(offset))
    }
    if (margin != null) {
      params.set('margin', String(margin))
    }
    if (viewType && viewType !== 'chronological') {
      params.set('viewType', viewType)
    }

    const query = params.toString()
    const url = `/api/v1/adventure/replies/${slug}${query ? `?${query}` : ''}`
    const response = await HttpClient().get(url)

    return {
      data: Array.isArray(response.data) ? response.data : [],
      meta: response.meta ?? null,
      viewType: response.viewType ?? viewType,
    }
  }
)

const adventuresSlice = createSlice({
  name: 'adventures',
  initialState,
  reducers: {
    /** Actualiza Master/GM en caliente tras claim/release/assign (sin esperar refetch). */
    applyAdventureGmUpdate(state, action) {
      if (!state.adventure) return
      const p = action.payload || {}
      if (p.slug && state.adventure.slug && p.slug !== state.adventure.slug) return
      const next = { ...state.adventure }
      if (Object.prototype.hasOwnProperty.call(p, 'masterMode')) {
        next.masterMode = p.masterMode
      }
      if (Object.prototype.hasOwnProperty.call(p, 'activeGm')) {
        next.activeGm = p.activeGm
      }
      if (Object.prototype.hasOwnProperty.call(p, 'canClaimGm')) {
        next.canClaimGm = !!p.canClaimGm
      }
      if (Object.prototype.hasOwnProperty.call(p, 'canReleaseGm')) {
        next.canReleaseGm = !!p.canReleaseGm
      }
      if (Object.prototype.hasOwnProperty.call(p, 'canAssignGm')) {
        next.canAssignGm = !!p.canAssignGm
      }
      if (Object.prototype.hasOwnProperty.call(p, 'canReassignGm')) {
        next.canReassignGm = !!p.canReassignGm
      }
      if (Object.prototype.hasOwnProperty.call(p, 'title') && p.title != null) {
        next.title = String(p.title)
      }
      if (Object.prototype.hasOwnProperty.call(p, 'isDeleted')) {
        next.isDeleted = !!p.isDeleted
      }
      if (Object.prototype.hasOwnProperty.call(p, 'deletedAt')) {
        next.deletedAt = p.deletedAt
      }
      if (Object.prototype.hasOwnProperty.call(p, 'canSoftDelete')) {
        next.canSoftDelete = !!p.canSoftDelete
      }
      if (Object.prototype.hasOwnProperty.call(p, 'canRestore')) {
        next.canRestore = !!p.canRestore
      }
      if (Object.prototype.hasOwnProperty.call(p, 'status')) {
        next.status = p.status
      }
      state.adventure = next
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdventure.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchAdventure.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const payload = action.payload
        // Evitar borrar la aventura en pantalla si el unwrap del cliente llega vacío
        if (!payload || (payload.slug == null && payload.adventureId == null)) {
          return
        }
        state.adventure = {
          ...payload,
          joined: state.joined,
        }
      })
      .addCase(fetchAdventure.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(joinAdventure.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(joinAdventure.fulfilled, (state, action) => {
        state.status = 'succeeded'
        if (action.payload.success !== false) {
          state.joined = true
          if (state.adventure) state.adventure.joined = true
        }
      })
      .addCase(joinAdventure.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
        state.joined = false
        if (state.adventure) state.adventure.joined = false
      })
      .addCase(leaveAdventure.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(leaveAdventure.fulfilled, (state) => {
        state.status = 'succeeded'
        state.joined = false
        if (state.adventure) state.adventure.joined = false
      })
      .addCase(leaveAdventure.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(getJoined.fulfilled, (state, action) => {
        state.joined = !!action.payload?.joined
        if (state.adventure) state.adventure.joined = state.joined
      })
      .addCase(getJoined.rejected, (state) => {
        state.joined = false
      })
  },
})

export const { applyAdventureGmUpdate } = adventuresSlice.actions
export default adventuresSlice.reducer
